import { DestroyRef, inject, Injectable } from '@angular/core';
import {
  ClientEvent,
  EventStatus,
  EventTimeline,
  EventType,
  MatrixClient,
  MatrixEvent,
  RoomEvent,
  RoomMember,
  RoomMemberEvent,
  SyncState,
  SyncStateData,
  TimelineWindow,
  User,
  UserEvent
} from 'matrix-js-sdk';
import {
  concatMap,
  EMPTY,
  expand,
  filter,
  first,
  from,
  map,
  merge,
  mergeMap,
  Observable,
  shareReplay,
  Subject,
  switchMap,
  tap
} from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatrixService } from '../matrix/matrix.service';
import { Room } from 'matrix-js-sdk/lib/models/room';
import { decryptionUpdates$, fromMatrixEvent } from './from-matrix-event';
import { UserPresence } from '../../model/user-presence';
import { RoomInfo } from '../../model/room-info';
import { MatrixRoomListService } from '../matrix-room-list/matrix-room-list.service';
import { KnownMembership } from 'matrix-js-sdk/lib/@types/membership.js';

@Injectable({ providedIn: 'root' })
/**
 * Room.timeline, Nachrichten/MatrixEvent-Stream.
 * Reaktives Streamen von Events mit Room.timeline.
 * Begründung: AFO-übergreifende Architektur
 */
export class SyncService {
  readonly message$: Observable<MatrixEvent>;
  readonly decrypted$: Observable<MatrixEvent>;
  readonly createRoom$: Observable<Room>;
  readonly deleteRoom$: Observable<string>;
  readonly roomInfos$: Observable<RoomInfo[]>;
  readonly membership$: Observable<[MatrixEvent, RoomMember]>;
  readonly presence$: Observable<UserPresence>;
  readonly typing$: Observable<{ roomId: string; members: string[] }>;
  readonly receipt$: Observable<void>;

  private readonly matrix = inject(MatrixService);
  private readonly matrixRoomListService = inject(MatrixRoomListService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly client$: Observable<MatrixClient>;
  private readonly resetStore$ = new Subject<void>();

  constructor() {
    this.client$ = this.matrix.getClientWithOrigin$().pipe(
      filter(clientWithOrigin => clientWithOrigin.origin === 'restoreSession' || clientWithOrigin.origin === 'logout'),
      tap(() => {
        // Setze ChatStore zurück auf Initialzustand (leer, ohne Räume) - insbes. auch bei Logout!
        this.resetStore$.next();
      }),
      filter(clientWithOrigin => clientWithOrigin.origin === 'restoreSession'),
      map(clientWithOrigin => clientWithOrigin.client!),
      shareReplay(1)
    );

    this.message$ = this.client$.pipe(
      switchMap(client =>
        merge(this.getAllEvents$(client), this.getConfirmedRedactions$(client), this.getConfirmedMessage$(client)).pipe(
          map(event => ({ client, event }))
        )
      ),
      concatMap(({ client, event }) => from(this.decryptEventIfNeeded(client, event))),
      takeUntilDestroyed(this.destroyRef)
    );

    this.decrypted$ = this.message$.pipe(
      filter(event => event.getType() === EventType.RoomMessageEncrypted),
      mergeMap(event => decryptionUpdates$(event))
    );

    this.createRoom$ = this.client$.pipe(switchMap(client => fromMatrixEvent<Room>(client, ClientEvent.Room)));

    this.deleteRoom$ = this.client$.pipe(switchMap(client => fromMatrixEvent<string>(client, ClientEvent.DeleteRoom)));

    // RoomInfos
    this.roomInfos$ = this.client$.pipe(
      switchMap(client => this.matrixRoomListService.getRoomInfos$(client)),
      shareReplay(1)
    );

    // Membership-Änderungen: Bei Invite ggf. automatisch blockieren
    this.membership$ = this.client$.pipe(
      switchMap(client => fromMatrixEvent<[MatrixEvent, RoomMember]>(client, RoomMemberEvent.Membership))
    );

    // Eigener User ist einem Raum beigetreten → Raum-Historie nachladen.
    this.membership$
      .pipe(
        filter(
          ([event, roomMember]) =>
            this.matrix.client.getUserId() === roomMember?.userId &&
            roomMember?.membership === KnownMembership.Join &&
            !!event.getRoomId()
        ),
        map(([event]) => event),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(async event => await this.loadRoomHistory(this.matrix.client, event.getRoomId()));

    // Typing: Typing-Events als Observable streamen (A_25431)
    this.typing$ = this.client$.pipe(
      switchMap(client =>
        fromMatrixEvent<[MatrixEvent, RoomMember]>(client, RoomMemberEvent.Typing).pipe(
          map(([, member]) => {
            const roomId = member.roomId ?? '';
            const room = client.getRoom(roomId);
            if (!room) {
              return { roomId, members: [] };
            }
            const currentUserId = client.getUserId();
            return {
              roomId,
              members: room
                .getMembers()
                .filter(m => m.userId !== currentUserId && m.typing)
                .map(m => m.rawDisplayName || m.userId)
            };
          })
        )
      )
    );

    // Receipt: Read-Receipt-Events als Observable streamen
    this.receipt$ = this.client$.pipe(
      switchMap(client => fromMatrixEvent<[MatrixEvent, Room]>(client, RoomEvent.Receipt).pipe(map(() => void 0)))
    );

    // Presence: Präsenz-Änderungen als Observable streamen
    this.presence$ = this.client$.pipe(
      switchMap(client =>
        fromMatrixEvent<[MatrixEvent | undefined, User]>(client, UserEvent.Presence).pipe(
          map(([, user]) => ({
            userId: user.userId,
            presence: user.presence as 'online' | 'offline' | 'unavailable',
            statusMsg: user.presenceStatusMsg
          })),
          shareReplay(1) // Cache letzten Wert für neue Subscriber
        )
      )
    );
  }

  getResetStore$(): Observable<void> {
    return this.resetStore$.asObservable();
  }

  /**
   * Server-bestätigte Redactions (RoomEvent.Redaction ohne Status/TxnId).
   * Diese Redactions sind endgültig, kein Rollback mehr möglich.
   * Dieses Event kommt nicht über RoomEvent.Timeline und wird deshalb separat gestreamt.
   */
  private getConfirmedRedactions$(client: MatrixClient): Observable<MatrixEvent> {
    return fromMatrixEvent<[MatrixEvent, Room, string | undefined]>(client, RoomEvent.Redaction).pipe(
      map(([event]) => event),
      filter(event => event.status === null && !event.getTxnId())
    );
  }

  private getConfirmedMessage$(client: MatrixClient): Observable<MatrixEvent> {
    return fromMatrixEvent<[MatrixEvent, Room, string | undefined, EventStatus | null]>(
      client,
      RoomEvent.LocalEchoUpdated
    ).pipe(
      filter(([event, , , oldStatus]) => oldStatus !== null && event.status === null),
      map(([event]) => event)
    );
  }

  /**
   * Hole alle Events aus allen momentan bestehenden Räumen sowie (zukünftige) Live-Events.
   */
  private getAllEvents$(client: MatrixClient): Observable<MatrixEvent> {
    // Warte, bis Sync fertig ist.
    const sync$ = fromMatrixEvent<[SyncState, SyncState | null, SyncStateData | undefined]>(
      client,
      ClientEvent.Sync
    ).pipe(
      map(([state]) => state),
      // Fallback, wenn PREPARED nie kommt: Auf SYNCING hören und über Rooms pollen.
      filter(state => state === SyncState.Prepared || (state === SyncState.Syncing && client.getRooms().length > 0)),
      first() // nur einmalig warten
    );

    // Lade alle Events aus allen Räumen.
    const history$ = sync$.pipe(
      switchMap(() =>
        // Hier werden alle left Rooms beachtet und ein neues sync angestoßen
        from(client.syncLeftRooms()).pipe(
          mergeMap(leftRooms =>
            merge(
              ...leftRooms.map(room =>
                this.loadAllEventsFromRoom(room, client).pipe(
                  // Flatten Array zu einzelnen Events.
                  mergeMap(events => from(events))
                )
              )
            )
          )
        )
      )
    );

    // Observable-Stream aller Timeline-Events eines Clients.
    // Gibt nur MatrixEvent zurück, ohne Room und toStartOfTimeline.
    const timelineEvents$ = fromMatrixEvent<[MatrixEvent, Room, boolean, boolean, unknown]>(
      client,
      RoomEvent.Timeline
    ).pipe(
      filter(([, room]) => !!room),
      map(([event]) => event)
    );

    // Kombiniere History + neue Events aus Live-Timeline.
    return merge(history$, timelineEvents$);
  }

  private loadAllEventsFromRoom(room: Room, client: MatrixClient): Observable<MatrixEvent[]> {
    const timelineSet = room.getTimelineSets()[0];
    const window = new TimelineWindow(client, timelineSet);

    const allEvents: MatrixEvent[] = [];

    return from(window.load()).pipe(
      expand(() =>
        window.canPaginate(EventTimeline.BACKWARDS) ? from(window.paginate(EventTimeline.BACKWARDS, 50)) : EMPTY
      ),
      map(() => {
        const current = window.getEvents();
        current.forEach(ev => {
          if (!allEvents.some(e => e.getId() === ev.getId())) {
            allEvents.push(ev);
          }
        });
        return allEvents;
      }),
      // Nur am Ende ausgeben (wenn Pagination fertig)
      first()
    );
  }

  /**
   * Speise (entschlüsseltes) MatrixEvent in Nachrichten-Stream (message$) ein.
   */
  private async decryptEventIfNeeded(client: MatrixClient, event: MatrixEvent): Promise<MatrixEvent> {
    // Falls Nachricht verschlüsselt, entschlüssele hier.
    if (event.getType() === EventType.RoomMessageEncrypted) {
      try {
        await client.decryptEventIfNeeded(event);
        // event.getType() jetzt m.room.message (aus clearEvent)
      } catch (e) {
        console.warn('Entschlüsselung fehlgeschlagen', e, event);
        // Trotzdem weiter
      }
    }

    return event;
  }

  /**
   * Neueste Events laden, dann bis zu 100 ältere nachladen.
   * Die geladenen Events feuern RoomEvent.Timeline und fließen über timelineEvents$ in den ChatStore.
   */
  private async loadRoomHistory(client: MatrixClient, roomId: string | undefined): Promise<void> {
    const room = client.getRoom(roomId);
    if (!room) {
      console.warn(`loadRoomHistory: Raum ${roomId} nicht gefunden.`);
      return;
    }

    const timelineSet = room.getTimelineSets()?.[0];
    if (!timelineSet) {
      console.warn(`loadRoomHistory: Kein TimelineSet für Raum ${roomId}.`);
      return;
    }

    try {
      const timelineWindow = new TimelineWindow(client, timelineSet);
      await timelineWindow.load();
      await timelineWindow.paginate(EventTimeline.BACKWARDS, 100);
    } catch (e) {
      console.warn(`loadRoomHistory: Fehler beim Laden der Historie für Raum ${roomId}.`, e);
    }
  }
}
